// Lib
import React, { useMemo } from 'react';
import classNames from 'classnames';

// Utils
import { getBackgroundColorClass } from '../utils/dataUtils/cell/tableCellStyleUtils';

// Components
import CellCheckboxRenderer from '../cellTypes/CellCheckboxRenderer';
import CellTextRenderer from './CellTextRenderer';
import CellFormulaErrorRenderer from './CellFormulaErrorRenderer';

// Hooks
import useMilanoteCellRendererFormulaInfo, { MilanoteHotTableInstance } from './useMilanoteCellRendererFormulaInfo';

// Constants
import { CellTypeConstants, CellTypeNames } from '../../../../common/table/CellTypeConstants';

// Types
import { CellData, HotComputedCellValue } from '../../../../common/table/TableTypes';

import './MilanoteCellRendererComponent.scss';

type Props = {
    row: number;
    col: number;
    hotComputedCellValue: HotComputedCellValue;
    cellData?: CellData;
    hotTableInstance: MilanoteHotTableInstance;
    hide?: boolean;
    filterQuery?: string;
};

const MilanoteCellRendererComponent: React.FC<Props> = (props) => {
    const { row, col, hotComputedCellValue, cellData, hotTableInstance, hide, filterQuery } = props;
    const { locale } = hotTableInstance.milanoteProps;

    const cellFormulaInfo = useMilanoteCellRendererFormulaInfo(props, [hotComputedCellValue, cellData, locale]);

    const backgroundClasses = useMemo(
        () => cellData && getBackgroundColorClass(cellData),
        [cellData, cellData?.background],
    );
    const className = classNames('MilanoteCellRendererComponent', backgroundClasses);

    if (!cellData) return null;
    if (hide) return null;

    if (cellFormulaInfo?.isFormulaError) {
        return (
            <div className={className}>
                <CellFormulaErrorRenderer cellError={cellFormulaInfo.hfCellValue} />
            </div>
        );
    }

    if (cellData.type?.name === CellTypeNames.CHECKBOX) {
        const alignment = cellData.textAlignment || CellTypeConstants[cellData.type.name].alignment;

        return (
            <div className={classNames(className, 'button-cell-renderer', `align-${alignment}`)}>
                <CellCheckboxRenderer
                    row={row}
                    col={col}
                    hotComputedCellValue={hotComputedCellValue}
                    isEditable={!cellFormulaInfo?.isFormula}
                    hotTableInstance={hotTableInstance}
                />
            </div>
        );
    }

    return (
        <div className={className}>
            <CellTextRenderer
                hotComputedCellValue={hotComputedCellValue}
                cellData={cellData}
                hotTableInstance={hotTableInstance}
                filterQuery={filterQuery}
                cellFormulaInfo={cellFormulaInfo}
            />
        </div>
    );
};

export default MilanoteCellRendererComponent;
